import React, { useState, useCallback } from "react";
import DatePicker from "react-datepicker";
import { CalendarDateRangeIcon } from "@heroicons/react/24/solid";
import "react-datepicker/dist/react-datepicker.css";
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subDays,
} from "date-fns";
import { Box, MenuItem, TextField, InputAdornment } from "@mui/material";

export default function DateRangeFilter({ onDateChange = () => {} }) {
  const [selectedOption, setSelectedOption] = useState("");
  const [customRange, setCustomRange] = useState([null, null]);
  const [startDate, endDate] = customRange;

  const updateDateRange = useCallback(
    (option) => {
      const today = new Date();
      let start, end;

      switch (option) {
        case "today":
          start = end = today;
          break;
        case "yesterday":
          start = end = subDays(today, 1);
          break;
        case "this_week":
          start = startOfWeek(today, { weekStartsOn: 1 });
          end = endOfWeek(today, { weekStartsOn: 1 });
          break;
        case "this_month":
          start = startOfMonth(today);
          end = endOfMonth(today);
          break;
        case "custom":
          return;
        default:
          return;
      }

      setCustomRange([start, end]);
      onDateChange({
        start: start.toISOString().split("T")[0],
        end: end.toISOString().split("T")[0],
      });
    },
    [onDateChange]
  );

  const handleSelectChange = (e) => {
    const value = e.target.value;
    setSelectedOption(value);

    if (value === "") {
      setCustomRange([null, null]);
      onDateChange({ start: "", end: "" });
      return;
    }

    if (value !== "custom") {
      setCustomRange([null, null]);
      updateDateRange(value);
    }
  };

  const handleCustomChange = (dates) => {
    setCustomRange(dates);
    if (dates[0] && dates[1]) {
      onDateChange({
        start: dates[0].toISOString().split("T")[0],
        end: dates[1].toISOString().split("T")[0],
      });
    } else {
      onDateChange({ start: "", end: "" });
    }
  };

  return (
    <Box
      display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      gap={2}
      width="100%"
    >
      {selectedOption === "custom" && (
        <Box sx={{ width: "100%", maxWidth: 250 }}>
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={handleCustomChange}
            isClearable
            placeholderText="Select a date range"
            maxDate={new Date()}
            customInput={
              <TextField
                label="Custom Range"
                fullWidth
                size="small"
                InputProps={{
                  readOnly: true,
                }}
              />
            }
          />
        </Box>
      )}

      <TextField
        select
        fullWidth
        size="small"
        label="Date Filter"
        value={selectedOption}
        onChange={handleSelectChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <CalendarDateRangeIcon className="h-5 w-5 text-gray-500" />
            </InputAdornment>
          ),
        }}
      >
        <MenuItem value="">Select Range</MenuItem>
        <MenuItem value="today">Today</MenuItem>
        <MenuItem value="yesterday">Yesterday</MenuItem>
        <MenuItem value="this_week">This Week</MenuItem>
        <MenuItem value="this_month">This Month</MenuItem>
        <MenuItem value="custom">Custom Range</MenuItem>
      </TextField>
    </Box>
  );
}
